import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  ContentLayout,
  Header,
  Container,
  SpaceBetween,
  ColumnLayout,
  Box,
  Button,
  KeyValuePairs,
  Table,
  StatusIndicator,
  Link,
  Spinner,
} from '@cloudscape-design/components';
import { useRules } from '../hooks/useRules';
import { useEvaluations } from '../hooks/useEvaluations';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { formatSubject, getStatusType, EVALUATION_STATUS_DISPLAY } from '../utils/evaluationHelpers';
import type { EvaluationSummary } from '@amzn/helianthus-arbiter-client';

export function DashboardPage() {
  useDocumentTitle('Dashboard');
  const navigate = useNavigate();
  const { rules, loading: rulesLoading } = useRules({});
  // Only the five most recent are rendered, so one page is all this card can ever show.
  const { evaluations: rawEvaluations, loading: evalsLoading } = useEvaluations({ limit: 50 }, { maxPages: 1 });
  const evaluations = useMemo(
    () => [...rawEvaluations].sort((a, b) => (b.startTime?.getTime() ?? 0) - (a.startTime?.getTime() ?? 0)).slice(0, 5),
    [rawEvaluations],
  );

  const ruleCounts = useMemo(() => {
    const counts: Record<string, number> = { active: 0, draft: 0, dry_run: 0, error: 0 };
    for (const rule of rules) {
      if (rule.ruleStatus && rule.ruleStatus in counts) {
        counts[rule.ruleStatus]++;
      }
    }
    return counts;
  }, [rules]);

  return (
    <ContentLayout
      headerVariant="high-contrast"
      header={
        <Header variant="h1" description="Security rule management overview">
          Dashboard
        </Header>
      }
    >
      <SpaceBetween size="l">
        <ColumnLayout columns={4}>
          <Container>
            <SpaceBetween size="s">
              <Header variant="h3">Create Rule</Header>
              <Box variant="p" color="text-body-secondary">
                Define a new security rule with inputs, evaluation logic, and severity.
              </Box>
              <Button variant="primary" onClick={() => navigate('/rules/create')}>
                Create Rule
              </Button>
            </SpaceBetween>
          </Container>
          <Container>
            <SpaceBetween size="s">
              <Header variant="h3">Run Evaluation</Header>
              <Box variant="p" color="text-body-secondary">
                Evaluate an AWS resource or code package against your active rules.
              </Box>
              <Button variant="primary" onClick={() => navigate('/evaluations/create')}>
                Run Evaluation
              </Button>
            </SpaceBetween>
          </Container>
          <Container>
            <SpaceBetween size="s">
              <Header variant="h3">Browse Rules</Header>
              <Box variant="p" color="text-body-secondary">
                View, search, and manage all existing security rules.
              </Box>
              <Button variant="primary" onClick={() => navigate('/rules')}>
                Browse Rules
              </Button>
            </SpaceBetween>
          </Container>
          <Container>
            <SpaceBetween size="s">
              <Header variant="h3">Arbiter MCP</Header>
              <Box variant="p" color="text-body-secondary">
                Use Arbiter directly from your IDE with the MCP integration. Evaluate resources and manage rules without leaving your editor.
              </Box>
              <Button
                variant="primary"
                iconAlign="right"
                iconName="external"
                onClick={() => window.open(
                  'https://console.harmony.a2z.com/mcp-registry/server/arbiter-mcp',
                  '_blank',
                  'noopener,noreferrer'
                )}
              >
                Get Started
              </Button>
            </SpaceBetween>
          </Container>
        </ColumnLayout>

        <ColumnLayout columns={2}>
          <Container
            header={<Header variant="h2">Rule Summary</Header>}
            footer={
              <Link href="/rules" onFollow={(e) => { e.preventDefault(); navigate('/rules'); }}>
                View all rules &rarr;
              </Link>
            }
          >
            {rulesLoading ? (
              <Spinner size="large" />
            ) : (
              <KeyValuePairs
                columns={2}
                items={[
                  { label: 'Active', value: <Box variant="awsui-key-label">{ruleCounts.active}</Box> },
                  { label: 'Draft', value: <Box variant="awsui-key-label">{ruleCounts.draft}</Box> },
                  { label: 'Dry Run', value: <Box variant="awsui-key-label">{ruleCounts.dry_run}</Box> },
                  { label: 'Error', value: <Box variant="awsui-key-label">{ruleCounts.error}</Box> },
                ]}
              />
            )}
          </Container>

          <Container
            header={<Header variant="h2">Recent Evaluations</Header>}
            footer={
              <Link href="/evaluations" onFollow={(e) => { e.preventDefault(); navigate('/evaluations'); }}>
                View all evaluations &rarr;
              </Link>
            }
          >
            <Table
              variant="embedded"
              loading={evalsLoading}
              loadingText="Loading evaluations..."
              items={evaluations}
              columnDefinitions={[
                {
                  id: 'subject',
                  header: 'Subject',
                  cell: (item: EvaluationSummary) => formatSubject(item),
                },
                {
                  id: 'status',
                  header: 'Status',
                  cell: (item: EvaluationSummary) => (
                    <StatusIndicator type={getStatusType(item.status!)}>
                      {EVALUATION_STATUS_DISPLAY[item.status!]}
                    </StatusIndicator>
                  ),
                },
                {
                  id: 'success',
                  header: 'Result',
                  cell: (item: EvaluationSummary) => {
                    if (item.outcome === 'PASS') {
                      return <StatusIndicator type="success">Pass</StatusIndicator>;
                    }
                    if (item.outcome === 'FAIL') {
                      return <StatusIndicator type="error">Fail</StatusIndicator>;
                    }
                    return <Box color="text-status-inactive">&mdash;</Box>;
                  },
                },
              ]}
              empty={
                <Box textAlign="center" color="inherit">
                  <b>No recent evaluations</b>
                </Box>
              }
            />
          </Container>
        </ColumnLayout>
      </SpaceBetween>
    </ContentLayout>
  );
}
